'use client';

import React, { useState, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  calculateKundliMatching,
  listBirthProfiles,
  BirthProfileRecord,
  MatchingResult,
  CreateBirthProfileInput
} from '@/lib/api/client';
import { useAuth } from '@/lib/auth/context';
import dynamic from 'next/dynamic';
import { LocationAutocomplete } from '@/components/quick-calculator/LocationAutocomplete';

const MatchingResultPanel = dynamic(
  () => import('./MatchingResultPanel').then((mod) => mod.MatchingResultPanel),
  {
    ssr: false,
    loading: () => (
      <div className="p-8 text-center rounded-2xl bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl animate-pulse text-xs text-[rgba(220,236,255,0.75)]">
        Loading result...
      </div>
    )
  }
);

interface KundliMatchingViewProps {
  headerTitle: string;
  headerDescription: string;
  calculateButtonLabel: string;
  contextNote?: string;
}

export function KundliMatchingView({
  headerTitle,
  headerDescription,
  calculateButtonLabel,
  contextNote
}: KundliMatchingViewProps) {
  const locale = useLocale();
  const tAshtakoot = useTranslations('ashtakoot');
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<BirthProfileRecord[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  const [mode, setMode] = useState<'saved' | 'manual'>('manual');
  const [selectedProfileA, setSelectedProfileA] = useState<string>('');
  const [selectedProfileB, setSelectedProfileB] = useState<string>('');

  const [personA, setPersonA] = useState<CreateBirthProfileInput>({
    name: locale === 'hi' ? 'प्रथम साथी' : 'Partner A',
    birthDate: '1990-01-01',
    birthTime: '12:00:00',
    latitude: 28.6139,
    longitude: 77.2090,
    timezone: 'Asia/Kolkata',
    locationName: locale === 'hi' ? 'नई दिल्ली' : 'New Delhi'
  });

  const [personB, setPersonB] = useState<CreateBirthProfileInput>({
    name: locale === 'hi' ? 'द्वितीय साथी' : 'Partner B',
    birthDate: '1992-05-15',
    birthTime: '15:30:00',
    latitude: 19.0760,
    longitude: 72.8777,
    timezone: 'Asia/Kolkata',
    locationName: locale === 'hi' ? 'मुंबई' : 'Mumbai'
  });

  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState<MatchingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setLoadingProfiles(true);
      listBirthProfiles()
        .then((res) => {
          setProfiles(res);
          if (res.length >= 2) {
            setMode('saved');
            setSelectedProfileA(res[0].id);
            setSelectedProfileB(res[1].id);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingProfiles(false));
    }
  }, [user]);

  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCalculating(true);
    setError(null);

    try {
      let res: MatchingResult;
      if (mode === 'saved' && selectedProfileA && selectedProfileB) {
        res = await calculateKundliMatching({
          personA: {
            name: profiles.find((p) => p.id === selectedProfileA)?.name || personA.name,
            birthDate: profiles.find((p) => p.id === selectedProfileA)?.birthDate || personA.birthDate,
            birthTime: profiles.find((p) => p.id === selectedProfileA)?.birthTime || personA.birthTime,
            latitude: profiles.find((p) => p.id === selectedProfileA)?.latitude || personA.latitude,
            longitude: profiles.find((p) => p.id === selectedProfileA)?.longitude || personA.longitude,
            timezone: profiles.find((p) => p.id === selectedProfileA)?.timezone || personA.timezone
          },
          personB: {
            name: profiles.find((p) => p.id === selectedProfileB)?.name || personB.name,
            birthDate: profiles.find((p) => p.id === selectedProfileB)?.birthDate || personB.birthDate,
            birthTime: profiles.find((p) => p.id === selectedProfileB)?.birthTime || personB.birthTime,
            latitude: profiles.find((p) => p.id === selectedProfileB)?.latitude || personB.latitude,
            longitude: profiles.find((p) => p.id === selectedProfileB)?.longitude || personB.longitude,
            timezone: profiles.find((p) => p.id === selectedProfileB)?.timezone || personB.timezone
          }
        });
      } else {
        res = await calculateKundliMatching({ personA, personB });
      }
      setResult(res);
    } catch (err: any) {
      setError(err.message || (locale === 'hi' ? 'गुण मिलान की गणना में त्रुटि हुई' : 'Failed to calculate matching score'));
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header Banner */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-2">
          {headerTitle}
        </h1>
        <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm max-w-2xl leading-[1.6]">
          {headerDescription}
        </p>
      </div>

      {contextNote && (
        <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4 text-xs text-[rgba(220,236,255,0.85)] italic text-center">
          {contextNote}
        </div>
      )}

      {/* Input Selection Form */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl">
        {user && profiles.length > 0 && (
          <div className="flex gap-2 sm:gap-3 mb-6 border-b border-white/10 pb-4">
            <button
              type="button"
              onClick={() => setMode('saved')}
              className={`px-4 py-2 text-xs font-semibold rounded-full transition-colors cursor-pointer ${
                mode === 'saved'
                  ? 'bg-white text-[#03163d] shadow-sm'
                  : 'text-[rgba(220,236,255,0.75)] hover:text-white hover:bg-white/10'
              }`}
            >
              {tAshtakoot('useSavedProfiles', { count: profiles.length })}
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`px-4 py-2 text-xs font-semibold rounded-full transition-colors cursor-pointer ${
                mode === 'manual'
                  ? 'bg-white text-[#03163d] shadow-sm'
                  : 'text-[rgba(220,236,255,0.75)] hover:text-white hover:bg-white/10'
              }`}
            >
              {tAshtakoot('enterBirthDetails')}
            </button>
          </div>
        )}

        <form onSubmit={handleCalculate} className="space-y-6">
          {mode === 'saved' && user && profiles.length >= 2 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('selectPartnerA')}</label>
                <select
                  value={selectedProfileA}
                  onChange={(e) => setSelectedProfileA(e.target.value)}
                  className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] [color-scheme:dark]"
                >
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id} className="bg-[#050a24] text-white">
                      {p.name} ({p.birthDate})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('selectPartnerB')}</label>
                <select
                  value={selectedProfileB}
                  onChange={(e) => setSelectedProfileB(e.target.value)}
                  className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] [color-scheme:dark]"
                >
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id} className="bg-[#050a24] text-white">
                      {p.name} ({p.birthDate})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Person A Form */}
              <div className="bg-white/[0.04] p-5 sm:p-6 rounded-2xl border border-white/12 space-y-4">
                <h3 className="text-sm font-heading font-semibold text-[#5ab4ff] border-b border-white/10 pb-2.5">
                  {tAshtakoot('partnerA')}
                </h3>
                <div>
                  <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.name')}</label>
                  <input
                    type="text"
                    value={personA.name}
                    onChange={(e) => setPersonA({ ...personA, name: e.target.value })}
                    className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.birthDate')}</label>
                    <input
                      type="date"
                      value={personA.birthDate}
                      onChange={(e) => setPersonA({ ...personA, birthDate: e.target.value })}
                      className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.birthTime')}</label>
                    <input
                      type="text"
                      value={personA.birthTime}
                      onChange={(e) => setPersonA({ ...personA, birthTime: e.target.value })}
                      className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.location')}</label>
                  <LocationAutocomplete
                    value={personA.locationName || ''}
                    onSelectLocation={(preset) => setPersonA({
                      ...personA,
                      locationName: preset.name,
                      latitude: preset.latitude,
                      longitude: preset.longitude,
                      timezone: preset.timezone
                    })}
                    onClearLocation={() => setPersonA({ ...personA, locationName: '' })}
                    confirmedPreset={personA.locationName ? {
                      name: personA.locationName,
                      latitude: personA.latitude,
                      longitude: personA.longitude,
                      timezone: personA.timezone
                    } : null}
                    idPrefix="partner-a-loc"
                  />
                </div>
              </div>

              {/* Person B Form */}
              <div className="bg-white/[0.04] p-5 sm:p-6 rounded-2xl border border-white/12 space-y-4">
                <h3 className="text-sm font-heading font-semibold text-[#5ab4ff] border-b border-white/10 pb-2.5">
                  {tAshtakoot('partnerB')}
                </h3>
                <div>
                  <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.name')}</label>
                  <input
                    type="text"
                    value={personB.name}
                    onChange={(e) => setPersonB({ ...personB, name: e.target.value })}
                    className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.birthDate')}</label>
                    <input
                      type="date"
                      value={personB.birthDate}
                      onChange={(e) => setPersonB({ ...personB, birthDate: e.target.value })}
                      className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.birthTime')}</label>
                    <input
                      type="text"
                      value={personB.birthTime}
                      onChange={(e) => setPersonB({ ...personB, birthTime: e.target.value })}
                      className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] transition-all [color-scheme:dark]"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">{tAshtakoot('form.location')}</label>
                  <LocationAutocomplete
                    value={personB.locationName || ''}
                    onSelectLocation={(preset) => setPersonB({
                      ...personB,
                      locationName: preset.name,
                      latitude: preset.latitude,
                      longitude: preset.longitude,
                      timezone: preset.timezone
                    })}
                    onClearLocation={() => setPersonB({ ...personB, locationName: '' })}
                    confirmedPreset={personB.locationName ? {
                      name: personB.locationName,
                      latitude: personB.latitude,
                      longitude: personB.longitude,
                      timezone: personB.timezone
                    } : null}
                    idPrefix="partner-b-loc"
                  />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-rose-500/10 border border-[#ff9bb0]/30 rounded-xl text-[#ff9bb0] text-xs">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={calculating}
            className="w-full h-12 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] transition-all disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
          >
            {calculating ? tAshtakoot('calculating') : calculateButtonLabel}
          </button>
        </form>
      </div>

      {/* Results View */}
      {result && <MatchingResultPanel result={result} />}
    </div>
  );
}
